/**
 * useProgressPolling Hook
 * Polls for progress updates during long-running operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import creatorWorkflowService from '../services/creatorWorkflowService';
import type { CourseDraft } from '../types/creator-workflow';

export interface ProgressData {
  progress_percent: number;
  current_item?: number;
  total_items?: number;
  status_message?: string;
  status_key?: string;
  status_params?: Record<string, string | number>;
  completed: boolean;
}

export interface UseProgressPollingOptions {
  draftId: string;
  stage: 'parsing' | 'audio_generation';
  shouldPoll: boolean;
  pollInterval?: number; // milliseconds
  onComplete?: () => void;
  onError?: (error: Error) => void;
  /** Called every poll cycle with the full draft — use to update parent state progressively */
  onDraftUpdate?: (draft: CourseDraft) => void;
}

export const useProgressPolling = ({
  draftId,
  stage,
  shouldPoll,
  pollInterval = 1000,
  onComplete,
  onError,
  onDraftUpdate,
}: UseProgressPollingOptions) => {
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastProgressRef = useRef<number>(-1);
  const lastProgressChangeTimeRef = useRef<number>(Date.now());
  const staleTimeoutMs = 8 * 60 * 1000; // 8 minutes with no progress change → timeout (writing-question gen can stall for ~5 min when Gemini is slow)

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    lastProgressRef.current = -1;
    lastProgressChangeTimeRef.current = Date.now();
  }, []);

  // Use refs for callbacks to avoid re-creating fetchProgress
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onDraftUpdateRef = useRef(onDraftUpdate);

  // Keep refs in sync
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onDraftUpdateRef.current = onDraftUpdate;
  }, [onDraftUpdate]);

  const fetchProgress = useCallback(async () => {
    try {
      const draft = await creatorWorkflowService.getDraft(draftId);

      // Notify caller with full draft so they can show progressive sentence results
      onDraftUpdateRef.current?.(draft);

      const stageProgress = draft.stage_progress?.[stage];

      if (!stageProgress) {
        // No progress data yet, continue polling
        return;
      }

      const progressData: ProgressData = {
        progress_percent: stageProgress.progress_percent || 0,
        current_item: stageProgress.current_item,
        total_items: stageProgress.total_items,
        status_message: stageProgress.status_message,
        status_key: stageProgress.status_key,
        status_params: stageProgress.status_params,
        completed: stageProgress.completed || false,
      };

      setProgress(progressData);

      // Check for backend failure (parsing_status === 'failed')
      if ((draft as any).parsing_status === 'failed' && stage === 'parsing') {
        const failMsg = (draft as any).parsing_error || 'Parsing failed on server';
        const failError = new Error(failMsg);
        setError(failError);
        stopPolling();
        onErrorRef.current?.(failError);
        return;
      }

      if (progressData.completed) {
        stopPolling();
        onCompleteRef.current?.();
        return;
      }

      // Stale progress timeout: only timeout if progress hasn't changed for 3 minutes
      const currentPercent = progressData.progress_percent || 0;
      if (currentPercent !== lastProgressRef.current) {
        lastProgressRef.current = currentPercent;
        lastProgressChangeTimeRef.current = Date.now();
      } else if (Date.now() - lastProgressChangeTimeRef.current > staleTimeoutMs) {
        const timeoutError = new Error(
          `Polling timeout: ${stage} progress stalled at ${currentPercent}% for over 3 minutes`
        );
        setError(timeoutError);
        stopPolling();
        onErrorRef.current?.(timeoutError);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      stopPolling();
      onErrorRef.current?.(error);
    }
  }, [draftId, stage, stopPolling]);

  // Use a ref to track polling state to avoid dependency issues
  const isPollingRef = useRef(false);

  // Start/stop polling based on shouldPoll
  useEffect(() => {
    if (shouldPoll && !isPollingRef.current) {
      isPollingRef.current = true;
      setIsPolling(true);
      lastProgressRef.current = -1;
      lastProgressChangeTimeRef.current = Date.now();

      // Immediate first fetch
      fetchProgress();

      // Set up interval
      pollingIntervalRef.current = setInterval(fetchProgress, pollInterval);
    } else if (!shouldPoll && isPollingRef.current) {
      isPollingRef.current = false;
      stopPolling();
    }

    return () => {
      if (isPollingRef.current) {
        isPollingRef.current = false;
        stopPolling();
      }
    };
  }, [shouldPoll, pollInterval, fetchProgress, stopPolling]);

  return {
    progress,
    isPolling,
    error,
    stopPolling,
  };
};
